import { useCallback } from 'react';
import { generatePagedQuery } from 'utils/string';

import useFetch from 'hooks/useFetch';

import { DEFAULT_PAGE_SIZE, PESTICIDE_LICENSES_API } from './constants';
import type {
    PagedResponsePesticideLicenseResponse,
    PesticideLicensePayload,
    PesticideLicenseResponse
} from './types';

const usePesticideLicenses = () => {
    const { authenticatedDelete, authenticatedGet, authenticatedPost, authenticatedPut } =
        useFetch();

    const fetchPesticideLicenses = useCallback(
        async (pageToken?: string | null, pageSize = DEFAULT_PAGE_SIZE) => {
            const queryParameters = generatePagedQuery(pageToken, pageSize);

            const response = await authenticatedGet(
                `${PESTICIDE_LICENSES_API}?${queryParameters.toString()}`,
                null
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }

            return response.data as PagedResponsePesticideLicenseResponse;
        },
        [authenticatedGet]
    );

    const fetchPesticideLicense = useCallback(
        async (licenseId: string) => {
            const response = await authenticatedGet(`${PESTICIDE_LICENSES_API}/${licenseId}`, null);

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }

            return response.data as PesticideLicenseResponse;
        },
        [authenticatedGet]
    );

    const createPesticideLicense = useCallback(
        async (payload: PesticideLicensePayload) => {
            const response = await authenticatedPost(
                PESTICIDE_LICENSES_API,
                JSON.stringify(payload)
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }
        },
        [authenticatedPost]
    );

    const updatePesticideLicense = useCallback(
        async (licenseId: string, payload: PesticideLicensePayload) => {
            const response = await authenticatedPut(
                `${PESTICIDE_LICENSES_API}/${licenseId}`,
                JSON.stringify(payload)
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }
        },
        [authenticatedPut]
    );

    const deletePesticideLicense = useCallback(
        async (licenseId: string) => {
            const response = await authenticatedDelete(
                `${PESTICIDE_LICENSES_API}/${licenseId}`,
                null
            );

            if (response.error) {
                throw new Error(JSON.stringify(response.error));
            }
        },
        [authenticatedDelete]
    );

    return {
        createPesticideLicense,
        deletePesticideLicense,
        fetchPesticideLicense,
        fetchPesticideLicenses,
        updatePesticideLicense
    };
};

export default usePesticideLicenses;
